import type { z } from "zod";
import { ApiErrorSchema } from "./schemas";
import { Sentry } from "./sentry";

const API_BASE = import.meta.env.VITE_SANDBAR_API_URL;

export class SandbarApiError extends Error {
  status: number;
  requestId: string;
  detail?: string;
  constructor(message: string, status: number, requestId: string, detail?: string) {
    super(message);
    this.name = "SandbarApiError";
    this.status = status;
    this.requestId = requestId;
    this.detail = detail;
  }
}

function newRequestId(): string {
  return `req_${crypto.randomUUID().replace(/-/g, "").slice(0, 24)}`;
}

export interface ApiFetchOptions extends RequestInit {
  token?: string | null;
}

export async function apiFetch<T>(
  path: string,
  schema: z.ZodType<T>,
  options?: ApiFetchOptions,
): Promise<T> {
  const requestId = newRequestId();
  const token = options?.token ?? (await window.Clerk?.session?.getToken()) ?? null;
  const headers: Record<string, string> = {
    "X-Request-Id": requestId,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.body ? { "Content-Type": "application/json" } : {}),
    ...((options?.headers as Record<string, string>) ?? {}),
  };

  let res: Response;
  // Under Vitest+jsdom, the jsdom AbortSignal prototype is not the one
  // undici's fetch validates against; drop the signal in test mode. In real
  // browsers and production the signal always passes through.
  const init: RequestInit = { ...options, headers };
  if (import.meta.env.MODE === "test") {
    delete (init as { signal?: AbortSignal }).signal;
  }
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch (err) {
    Sentry.withScope((s) => {
      s.setTag("request_id", requestId);
      s.setExtra("api_path", path);
      s.captureMessage(`API network error: ${path}`, "error");
    });
    throw new SandbarApiError(err instanceof Error ? err.message : "Network error", 0, requestId);
  }

  if (!res.ok) {
    let detail: string | undefined;
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = ApiErrorSchema.parse(await res.json());
      detail = body.detail;
      message = body.error;
    } catch {
      /* response body wasn't JSON or didn't match — keep status message */
    }
    Sentry.withScope((s) => {
      s.setTag("request_id", requestId);
      s.setExtra("api_path", path);
      s.setExtra("status", res.status);
      s.captureMessage(`API error ${res.status}: ${path}`, "warning");
    });
    if (res.status === 401) {
      await window.Clerk?.signOut?.();
      window.location.assign("/sign-in");
    }
    throw new SandbarApiError(message, res.status, requestId, detail);
  }

  // DELETE / activate / portal mutations often return empty bodies (204 or
  // 200 with no payload). Read as text first so we don't throw
  // "Unexpected end of JSON input" — then parse only if there's content.
  // Callers that pass z.unknown() get undefined; callers expecting a real
  // shape get a clear Zod failure instead of a cryptic fetch error.
  const text = await res.text();
  if (text.length === 0) {
    return schema.parse(undefined);
  }
  return schema.parse(JSON.parse(text));
}

export function assignIfStripe(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  if (!parsed.host.endsWith(".stripe.com") && parsed.host !== "stripe.com") return false;
  window.location.assign(parsed.toString());
  return true;
}

declare global {
  interface Window {
    Clerk?: {
      session?: { getToken: () => Promise<string | null> };
      signOut?: () => Promise<void>;
    };
  }
}
